import { FunctionComponent, useCallback, useEffect, useState } from "react"

import { DndContext, rectIntersection } from "@dnd-kit/core"
import { restrictToWindowEdges } from "@dnd-kit/modifiers"
import { useParams } from "react-router-dom"
import { useGetUserapiAdminTestsGetTestByIdByTestIdQuery } from "../../../app/services/meseeknaApi"
import {
  RootState,
  useTypedDispatch,
  useTypedSelector,
} from "../../../app/store"
import HeaderSection from "../../../components/header-section"
import MessagesWithSearch from "../../../components/messages-with-search"
import SidebarMinimal from "../../sidebar/components/sidebar-minimal"
import SimulationHeader from "../../../components/simulation-header"
import DecisionPlanSection from "../../simulation-decisions/components/decision-plan-section"
import ModalBreaktime from "../../simulation/components/modal-breaktime"
import { simulationSlice } from "../../simulation/simulationSlice"
import {
  createTimer,
  destroyTimer,
  resetSimulationSafe,
  resumeTimer,
} from "../../simulation/simulationThunks"
import { convertToSeconds } from "../../simulation/utils"
import { setAlertsForRunId } from "../../user-alerts/userAlertsSlice"
import Tutorial from "./tutorial"

const TutorialBase: FunctionComponent = () => {
  const dispatch = useTypedDispatch()
  const { testId: testIdParam, runId: runIdParam } = useParams()
  const runId = parseInt(runIdParam as string)

  const [isModalBreaktimeOpen, setModalBreaktimeOpen] = useState(false)

  const { duration, timer, secondsElapsed } = useTypedSelector(
    (state: RootState) => {
      return state.simulationSlice
    },
  )

  const testId = parseInt(testIdParam as string)

  const closeModalBreaktime = useCallback(() => {
    dispatch(resumeTimer())
    setModalBreaktimeOpen(false)
  }, [])

  const {
    data: simResponse,
    isSuccess: simLoadIsSuccess,
    isFetching: simLoadIsFetching,
  } = useGetUserapiAdminTestsGetTestByIdByTestIdQuery({
    testId: testId,
  })

  useEffect(() => {
    // Clear any persisted state to prevent cross-contamination
    localStorage.removeItem("persist:msk:simulation")
    dispatch(resetSimulationSafe())

    // Also clear out any user alerts. We can assume if you are doing
    // the tutorial, prior alerts don't matter.
    dispatch(setAlertsForRunId({ runId, alerts: [] }))

    return () => {
      // Just destroy timer on unmount - simulation clears state on its own mount
      dispatch(destroyTimer())
    }
  }, [])

  useEffect(() => {
    console.debug("Sim load success?", simLoadIsSuccess)
    if (!simLoadIsSuccess) return
    dispatch(simulationSlice.actions.setupSimulation(simResponse))
    dispatch(simulationSlice.actions.setEvents([]))
  }, [simLoadIsFetching, simLoadIsSuccess, simResponse, dispatch])

  useEffect(() => {
    if (!simLoadIsSuccess) return
    if (!timer) {
      console.debug("creating timer")
      dispatch(createTimer())
    } else {
      console.debug("timer already exists")
      dispatch(resumeTimer())
    }
    return () => {
      dispatch(destroyTimer())
    }
  }, [simLoadIsFetching, simLoadIsSuccess])

  // Monitor simulation time, end sim if time is up
  useEffect(() => {
    if (secondsElapsed === 0) return
    if (!simLoadIsSuccess) return
    if (!timer) return
    const durationSeconds = convertToSeconds(duration)
    if (durationSeconds === 0) return
  }, [secondsElapsed])

  return (
    <div className="relative bg-base-white w-full h-[100dvh] overflow-hidden flex flex-row items-start justify-start font-text-md-regular">
      <SidebarMinimal />
      <div className="w-full h-[100dvh] flex-1 flex flex-col">
        <HeaderSection />
        <div
          id="simulationContainer"
          className="relative overflow-hidden box-border w-full flex-1 min-h-0 flex flex-col justify-start py-6 px-9 gap-[19px] text-left text-sm text-gray-600 font-text-sm-medium border-r-[1px] border-solid border-gray-200">
          <Tutorial />
          <SimulationHeader
            setModalBreaktimeOpen={setModalBreaktimeOpen}
            simulation={simResponse?.data ? simResponse?.data : []}
          />
          <div className="w-full overflow-hidden flex-1 min-h-0 flex flex-col items-start justify-start py-0 px-0 box-border gap-[12px] text-base text-gray-700">
            <DndContext
              autoScroll={false}
              collisionDetection={rectIntersection}
              modifiers={[restrictToWindowEdges]}>
              <div
                id="boxesContainer"
                className="self-stretch overflow-hidden flex-1 min-h-0 flex flex-row items-start gap-[19px] text-sm text-gray-500">
                <MessagesWithSearch />
                <DecisionPlanSection />
              </div>
            </DndContext>
          </div>
        </div>
      </div>
      {isModalBreaktimeOpen && (
        <ModalBreaktime closeModalBreaktime={closeModalBreaktime} />
      )}
    </div>
  )
}

export default TutorialBase
